import type {
	OfferingMaskImageModel,
	OfferingModel,
	OfferingOverlayImageModel,
} from '@sosocio/creator-types/database';

export type OfferingPageAssetKind = 'mask' | 'overlay';

export type OfferingPageAssetModel = (
	OfferingMaskImageModel
	| OfferingOverlayImageModel
);

export interface OfferingPageAssetSource<Model extends OfferingPageAssetModel> {
	cacheKey: string;
	model?: Model;
	url: string;
}

interface ResolveOfferingPageAssetSourceOptions<Model extends OfferingPageAssetModel> {
	assetKind: OfferingPageAssetKind;
	fallbackUrl?: string | null;
	models: ReadonlyArray<Model>;
	offeringId: OfferingModel['id'];
	pageIndex: number;
}

/**
 * Finds the page-specific mask or overlay associated with an offering.
 * Page indexes remain zero-based to match the persisted offering asset contract.
 *
 * @param models Candidate {@link OfferingPageAssetModel} records from the host catalog.
 * @param offeringId Offering identifier that owns the requested asset.
 * @param pageIndex Zero-based page index within the offering.
 * @returns Matching asset model, or `undefined` when the catalog has no exact match.
 */
export function findOfferingPageAssetModel<Model extends OfferingPageAssetModel>(
	models: ReadonlyArray<Model>,
	offeringId: OfferingModel['id'],
	pageIndex: number,
): Model | undefined {
	return models.find((model) => (
		model.offeringid === offeringId
		&& model.pageindex === pageIndex
	));
}

/**
 * Resolves a page-aware offering asset with a stable cache identity, falling
 * back to the legacy offering-level source when no catalog model exists.
 *
 * @param options {@link ResolveOfferingPageAssetSourceOptions} describing the asset catalog and fallback.
 * @returns Resolved {@link OfferingPageAssetSource}, or `null` when neither source is configured.
 */
export function resolveOfferingPageAssetSource<Model extends OfferingPageAssetModel>(
	options: ResolveOfferingPageAssetSourceOptions<Model>,
): OfferingPageAssetSource<Model> | null {
	const model = findOfferingPageAssetModel(
		options.models,
		options.offeringId,
		options.pageIndex,
	);

	if (model) {
		return {
			cacheKey: `${options.assetKind}:${String(model.id)}`,
			model,
			url: model.url,
		};
	}

	if (!options.fallbackUrl) {
		return null;
	}

	return {
		cacheKey: `${options.assetKind}:legacy:${options.offeringId}`,
		url: options.fallbackUrl,
	};
}
